import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { Call, CallsService, Sentence, Speaker, SpeakerMapping, Transcript, TranscriptSpeakerSlot, MetricType } from '../../calls.service'
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu'
import { MatIcon } from '@angular/material/icon'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { formatSpeaker, SpeakerPipe } from '../../speaker.pipe'
import { ToastService } from '../../toast.service'
import { AvatarComponent } from '../../avatar/avatar.component'
import { MatTooltip } from '@angular/material/tooltip'
import { MatDialog } from '@angular/material/dialog'
import { AddSpeakerDialogComponent, AddSpeakerDialogData } from '../../add-speaker-dialog/add-speaker-dialog.component'
import { filter, map, Subscription } from 'rxjs'
import { uniqueBy } from '../../utils'
import { LoggerService } from '../../logger.service'
import { TimestampPipe } from '../../timestamp.pipe'
import { DurationPipe } from '../../duration.pipe'
import { MetricsService } from '../../metrics.service'
import { RecordingClockPipe } from '../../recording-clock.pipe'
import { DatePipe } from '@angular/common'

type IsHighlightedMap = { [key: string]: boolean }

@Component({
  selector: 'app-transcript',
  standalone: true,
  imports: [
    MatIcon,
    MatMenuModule,
    TranslatePipe,
    TranslateDirective,
    SpeakerPipe,
    AvatarComponent,
    MatTooltip,
    TimestampPipe,
    DurationPipe,
    RecordingClockPipe,
    DatePipe,
  ],
  templateUrl: './transcript.component.html',
  styleUrls: ['./transcript.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TranscriptComponent implements OnInit, OnDestroy {
  @Input({ required: true })
  transcript!: Transcript

  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  canEdit!: boolean

  @Input()
  isSensitiveModeEnabled: boolean = false

  @Input()
  showClockTime: boolean = false

  maskSentenceContent(content: string): string {
    return content.replace(/\S/g, '*')
  }

  @Output()
  selectSentence = new EventEmitter<Sentence>()

  @ViewChildren('menuTrigger')
  menuTriggers!: QueryList<MatMenuTrigger>

  selectionStartSentenceId: string | null = null
  selectionEndSentenceId: string | null = null

  changeDetectorRef = inject(ChangeDetectorRef)
  elementRef = inject(ElementRef)
  route = inject(ActivatedRoute)
  callsService = inject(CallsService)
  toastService = inject(ToastService)
  metricsService = inject(MetricsService)
  dialog = inject(MatDialog)
  logger = inject(LoggerService)
  assignSentencesToSpeakerMutation = this.callsService.assignSentencesToSpeakerMutation()
  addSpeakerMutation = this.callsService.addSpeakerMutation()
  isHighlighted: IsHighlightedMap = {}
  sentenceIdSubscription: Subscription | null = null
  sentenceIdsList: string[] = []
  lastClickWithinTranscript = false

  isPowerUserModeEnabled = false
  isPowerUserModeEnabledSubscription = inject(ActivatedRoute)
    .queryParamMap.pipe(map((params) => params.get('isPowerUserModeEnabled') === 'true'))
    .subscribe((value) => (this.isPowerUserModeEnabled = value))

  get effectiveSpeakerMapping(): SpeakerMapping {
    return this.call.userSpeakerMapping ?? {}
  }

  getSpeakerDropdownLabel(speaker: Speaker): string {
    return formatSpeaker(speaker, this.effectiveSpeakerMapping)
  }

  get uniqueSpeakers(): Speaker[] {
    const speakerMapping = this.effectiveSpeakerMapping
    const getDisplayName = (speaker: Speaker) => formatSpeaker(speaker, speakerMapping)

    const allSpeakersSorted = this.call.speakers
      .filter((speaker) => speaker.speakerTag !== '')
      .filter((speaker) => speaker.name?.trim() !== '')
      .toSorted((a: Speaker, b: Speaker) => getDisplayName(a).localeCompare(getDisplayName(b)))
    const uniqueAllSpeakerSorted = uniqueBy(allSpeakersSorted, 'speakerTag')

    if (this.selectionStartSentenceId === null || this.selectionEndSentenceId === null) {
      return uniqueAllSpeakerSorted
    }

    const selectedSpeakers = this.transcriptSpeakerSlots
      .filter((slot) =>
        slot.sentences.some((sentence) => sentence.sentenceId >= this.selectionStartSentenceId! && sentence.sentenceId <= this.selectionEndSentenceId!)
      )
      .map((slot) => slot.speaker)

    // If there are multiple speakers selected, show all of them to allow selecting one of them
    const multipleSpeakersSelected = selectedSpeakers.length > 1
    const noSpeakersSelected = selectedSpeakers.length === 0
    if (multipleSpeakersSelected || noSpeakersSelected) {
      return uniqueAllSpeakerSorted
    }

    const selectedSpeaker = selectedSpeakers[0]

    return uniqueBy(
      this.call.speakers
        .filter((speaker) => speaker.speakerTag !== '' && selectedSpeaker.name !== speakerMapping[speaker.speakerTag])
        .toSorted((a: Speaker, b: Speaker) => getDisplayName(a).localeCompare(getDisplayName(b))),
      'speakerTag'
    )
  }

  ngOnInit() {
    this.sentenceIdsList = this.transcriptSpeakerSlots.map((slot) => slot.sentences.map((sentence) => sentence.sentenceId)).flat()

    this.sentenceIdSubscription = this.route.queryParams.subscribe((params) => {
      const sentenceId = params['sentenceId'] ?? null
      const endSentenceId = params['endSentenceId']
      const highlight = params['highlight'] === 'true'
      if (!highlight) {
        this.removeHighlights()
        return
      }
      if (sentenceId !== null) {
        setTimeout(() => this.highlightSentences(sentenceId, endSentenceId ?? sentenceId))
      }
    })
  }

  ngOnDestroy() {
    if (this.sentenceIdSubscription !== null) {
      this.sentenceIdSubscription.unsubscribe()
    }
    this.isPowerUserModeEnabledSubscription.unsubscribe()
  }

  @HostListener('document:mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    const target = event.target as Element
    this.lastClickWithinTranscript = this.elementRef.nativeElement.contains(target)
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const isSelectAll = (event.ctrlKey || event.metaKey) && event.key === 'a'
    if (!isSelectAll) {
      return
    }

    const activeElement = document.activeElement
    const isInputOrTextarea = activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA'
    if (isInputOrTextarea) {
      return
    }

    if (this.lastClickWithinTranscript) {
      event.preventDefault()
    }
  }

  @HostListener('document:selectionchange')
  onSelectionChange() {
    const selection = window.getSelection()
    const selectionStartParent = selection?.anchorNode?.parentElement?.closest('.sentence')
    const selectionEndParent = selection?.focusNode?.parentElement?.closest('.sentence')

    const startSentence = selectionStartParent?.id?.split('-')?.at(1) ?? null
    const endSentence = selectionEndParent?.id?.split('-')?.at(1) ?? null

    if (startSentence === null || endSentence === null) {
      this.selectionStartSentenceId = null
      this.selectionEndSentenceId = null
      return
    }

    const normalizedStart = startSentence > endSentence ? endSentence : startSentence
    const normalizedEnd = startSentence > endSentence ? startSentence : endSentence

    const startSlot = this.findSpeakerSlotForSentence(normalizedStart)
    const endSlot = this.findSpeakerSlotForSentence(normalizedEnd)

    if (startSlot !== endSlot) {
      selection?.removeAllRanges()
      this.selectionStartSentenceId = null
      this.selectionEndSentenceId = null
      return
    }

    this.selectionStartSentenceId = normalizedStart
    this.selectionEndSentenceId = normalizedEnd
  }

  private findSpeakerSlotForSentence(sentenceId: string): TranscriptSpeakerSlot | null {
    return this.transcriptSpeakerSlots.find((slot) => slot.sentences.some((sentence) => sentence.sentenceId === sentenceId)) ?? null
  }

  get transcriptSpeakerSlots(): TranscriptSpeakerSlot[] {
    return this.transcript.filter((slot) => slot.type === 'speaker') as TranscriptSpeakerSlot[]
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    const selection = window.getSelection()
    if (selection === null || selection.toString().length === 0 || this.selectionEndSentenceId === null) {
      return
    }
    const menuTrigger =
      this.menuTriggers.find((trigger) => {
        const sentenceId = trigger['_element'].nativeElement.getAttribute('data-sentence-id')
        return sentenceId === this.selectionEndSentenceId
      }) ?? null
    if (menuTrigger !== null) {
      menuTrigger.openMenu()
    } else {
      console.log('No menu trigger found for selectionEndSentenceId:', this.selectionEndSentenceId)
    }
  }

  highlightSentences(startSentenceId: string, endSentenceId: string) {
    this.isHighlighted = this.sentenceIdsList.reduce((acc: Record<string, boolean>, sentenceId) => {
      if (sentenceId >= startSentenceId && sentenceId <= endSentenceId) {
        acc[sentenceId] = true
      }
      return acc
    }, {})
    this.changeDetectorRef.markForCheck()
  }

  removeHighlights() {
    this.isHighlighted = {}
    this.changeDetectorRef.markForCheck()
  }

  copyRich() {
    if (this.selectionStartSentenceId === null || this.selectionEndSentenceId === null) {
      // TODO Make error logging
      return
    }
    let plainString = ''
    let htmlString = ''
    const meetingDate = this.call.date
    const meetingName = this.call.name
    plainString = plainString + meetingName + ', ' + meetingDate.toString()?.slice(0, 10) + '\n'
    htmlString = htmlString + '<b>' + meetingName + ', ' + meetingDate.toString()?.slice(0, 10) + '</b>' + '<br>'

    const matchingSlots = this.transcriptSpeakerSlots.filter((slot) =>
      slot.sentences.some((sentence) => sentence.sentenceId >= this.selectionStartSentenceId! && sentence.sentenceId <= this.selectionEndSentenceId!)
    )
    let startSentence = null
    for (const slot of matchingSlots) {
      const speaker = formatSpeaker(slot.speaker, this.effectiveSpeakerMapping)
      const sentences = slot.sentences.filter(
        (sentence) => sentence.sentenceId >= this.selectionStartSentenceId! && sentence.sentenceId <= this.selectionEndSentenceId!
      )
      if (startSentence === null && sentences.length > 0) {
        startSentence = sentences[0]
      }
      plainString = plainString + speaker + ': ' + "'" + this.clearStringForRichCopy(sentences.map((sentence) => sentence.content).join(' ')) + "'" + '\n'
      htmlString =
        htmlString + '<u>' + speaker + ':' + '</u>' + " '" + this.clearStringForRichCopy(sentences.map((sentence) => sentence.content).join(' ')) + "'" + '<br>'
    }
    const baseUrl = window.location.origin
    const mediaTime = startSentence?.startTimeSeconds.toFixed(2)
    const url = `${baseUrl}/app/meetings/${this.call.id}` + (mediaTime ? `?mediaTime=${mediaTime}` : '')
    plainString = plainString + url + '\n'
    htmlString = `${htmlString}<a href="${url}">${url}</a><br>`
    if (window.ClipboardItem === undefined) {
      // Fallback for local development
      console.info('ClipboardItem is not available without HTTPS. The next log messages contain the rich and plain text that would be copied to the clipboard.')
      console.info(htmlString)
      console.info(plainString)
      return
    }
    const clipboardItem = new window.ClipboardItem({
      'text/html': new Blob([htmlString], { type: 'text/html' }),
      'text/plain': new Blob([plainString], { type: 'text/plain' }),
    })
    navigator.clipboard.write([clipboardItem])
  }

  private clearStringForRichCopy(str: string): string {
    const result = str
      .replace(/[\n\r][\n\r]+/g, ' ')
      .replace(/(.)([0-9]+:[0-9]+:[0-9]+)/g, '.\n\n$2')
      .replace(/\([0-9]+%\)/g, '')
      .replace(/( (\.|,|\?|!|;|:| ))/g, '$2')
    return result
  }

  async updateSpeaker(speakerTag: string): Promise<void> {
    if (this.selectionStartSentenceId === null || this.selectionEndSentenceId === null) {
      // TODO Make error logging
      return
    }
    await this.assignSentencesToSpeakerMutation.mutateAsync({
      callId: this.call.id,
      startSentenceId: this.selectionStartSentenceId,
      endSentenceId: this.selectionEndSentenceId,
      speakerTag,
    })
    this.metricsService.sendMetric(this.call.id, MetricType.ASSIGN_SPEAKER_VIA_TRANSCRIPT_SELECTION)
    this.selectionStartSentenceId = null
    this.selectionEndSentenceId = null
    this.toastService.show('CALLS.SPEAKER_UPDATED_NOTIFICATION')
  }

  async addSpeaker() {
    if (this.selectionStartSentenceId === null || this.selectionEndSentenceId === null) {
      return
    }
    const selectionStartSentenceId = this.selectionStartSentenceId
    const selectionEndSentenceId = this.selectionEndSentenceId
    const dialogData: AddSpeakerDialogData = { attendeeNames: this.call.attendeeNames }
    const dialogRef = this.dialog.open(AddSpeakerDialogComponent, {
      data: dialogData,
    })
    dialogRef
      .afterClosed()
      .pipe(filter((result): result is string => typeof result === 'string' && result.length > 0))
      .subscribe(async (speakerName) => {
        const existingSpeakerTag = this.findSpeakerTagByName(speakerName)

        if (existingSpeakerTag !== null) {
          await this.assignSentencesToSpeakerMutation.mutateAsync({
            callId: this.call.id,
            startSentenceId: selectionStartSentenceId,
            endSentenceId: selectionEndSentenceId,
            speakerTag: existingSpeakerTag,
          })
          this.metricsService.sendMetric(this.call.id, MetricType.ASSIGN_SPEAKER_VIA_TRANSCRIPT_SELECTION)
          this.toastService.show('CALLS.SPEAKER_UPDATED_NOTIFICATION')
        } else {
          await this.addSpeakerMutation.mutateAsync({
            callId: this.call.id,
            startSentenceId: selectionStartSentenceId,
            endSentenceId: selectionEndSentenceId,
            speakerName,
          })
          this.metricsService.sendMetric(this.call.id, MetricType.ASSIGN_SPEAKER_VIA_TRANSCRIPT_SELECTION)
          this.toastService.show('CALLS.SPEAKER_ADDED_NOTIFICATION')
        }
      })
  }

  async assignToAttendee(attendeeName: string): Promise<void> {
    const existingSpeakerTag = this.findSpeakerTagByName(attendeeName)
    if (existingSpeakerTag !== null) {
      await this.updateSpeaker(existingSpeakerTag)
      return
    }
    if (this.selectionStartSentenceId === null || this.selectionEndSentenceId === null) {
      this.toastService.show('CALLS.NO_SELECTION_ERROR')
      return
    }
    await this.addSpeakerMutation.mutateAsync({
      callId: this.call.id,
      startSentenceId: this.selectionStartSentenceId,
      endSentenceId: this.selectionEndSentenceId,
      speakerName: attendeeName,
    })
    this.metricsService.sendMetric(this.call.id, MetricType.ASSIGN_SPEAKER_VIA_TRANSCRIPT_SELECTION)
    this.selectionStartSentenceId = null
    this.selectionEndSentenceId = null
    this.toastService.show('CALLS.SPEAKER_ADDED_NOTIFICATION')
  }

  get filteredAttendeeNames(): string[] {
    const assignedSpeakerNames = Object.values(this.call.userSpeakerMapping ?? {})
    return this.call.attendeeNames.filter((name) => !assignedSpeakerNames.includes(name)).toSorted((a, b) => a.localeCompare(b))
  }

  private findSpeakerTagByName(speakerName: string): string | null {
    return Object.entries(this.call.userSpeakerMapping ?? {}).find(([, name]) => name === speakerName)?.[0] ?? null
  }

  handleClickEvent(sentence: Sentence) {
    const currentSelection = window.getSelection()
    if (currentSelection === null || currentSelection.toString().length === 0) {
      this.selectSentence.emit(sentence)
    }
  }
}
